// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Route, Switch } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../../api';

// Components
// import ViewControl from '../ViewControl';
import Navigation from '../Navigation';
import User from '../User';
import TeamSettings from './TeamSettings';
import InboundEmail from './InboundEmail';
import InboundFax from './InboundFax';
import Billing from './Billing';
import Sync from './Sync';
import Automate from './Automate';
import Forms from './Forms';
import Scripts from './Scripts';
import PlanEditor from './PlanEditor';

// Modals
import InviteUser from '../Modals/InviteUser';
import AddEmailAccount from '../Modals/AddEmailAccount';
import ConfigureEmail from '../Modals/ConfigureEmail';
import ConfirmEmail from '../Modals/ConfirmEmail';
import ConfirmEmailYear from '../Modals/ConfirmEmailYear';
import CancelImport from '../Modals/CancelImport';
import ConfirmCaution from '../Modals/ConfirmCaution';
import AttachFromMarketplace from '../Modals/Automate/AttachFromMarketplace';
import ConfigureModule from '../Modals/Automate/ConfigureModule';
import EditForm from '../Modals/EditForm';
import CreateForm from '../Modals/CreateForm';
import CreateScript from '../Modals/CreateScript';
import EditScript from '../Modals/EditScript';

// Import/Sync
import SyncDropbox from '../Modals/Sync/Dropbox';
import SyncOneDrive from '../Modals/Sync/OneDrive';

import SyncGoogleDrive from '../Modals/Sync/GoogleDrive';

import SyncBox from '../Modals/Sync/Box';
import SyncSmartVault from '../Modals/Sync/SmartVault';
import SyncEgnyte from '../Modals/Sync/Egnyte';
import SyncShareFile from '../Modals/Sync/ShareFile';

import SyncSharePoint from '../Modals/Sync/SharePoint';
// import SyncPensionPro from '../Modals/Sync/PensionPro';
// import SyncJohnHancock from '../Modals/Sync/JohnHancock';
// import SyncEmpower from '../Modals/Sync/Empower';
// import SyncVoya from '../Modals/Sync/Voya';
// import SyncAscensus from '../Modals/Sync/Ascensus';
// import SyncAmericanFundsRK from '../Modals/Sync/AmericanFundsRK';
// import SyncAmericanFundsPP from '../Modals/Sync/AmericanFundsPP';
import SyncDesktop from '../Modals/Sync/Desktop';

// Tips
import EmailTips from '../Tips/EmailTips';

// Styles
import '../../styles/settings.css';
import RunTA from '../Modals/RunTA';

export default class Settings extends React.Component {

    state = {

        // view: 'grid',
        changed: false,

        // Shared team data
        modules: [],
        stacks: [],
        forms: [],
        scripts: [],

        // Modal controller
        modal: "",
        modalData: null

    }

    modal = async (modal = "", modalData = {}) => {
        await this.setState({ modal, modalData });
    }

    componentDidMount = () => {
        this.initGoogle()

        if (this.props.user && this.props.user._id)
            this.ensureAdmin(this.props.user);

        else this.props.loading(1005, '', false, 'settings');
    }

    componentWillReceiveProps = (next) => {
        if (next.user && next.user._id && next.user._id !== this.props.user._id)
            this.ensureAdmin(next.user);
    }

    loadStacks = async (modules = false) => {
        const res = await api.get('/stack/all?minimal=true' + (modules ? '&modules=true' : ''));
        if (res && res.success && res.stacks)
            this.setState({ stacks: res.stacks });
    }

    ensureAdmin = (user) => {
        this.props.loaded('settings');
        // Some settings page's we need to allow users to go to: /plan-editor and /automate
        if (this.props.location.pathname === "/settings/team" && !user.isAdmin) {
            this.props.history.push('/');
            return;
        }
    }

    createForm = async (formName) => {
        await this.props.loading(1005, '', false, 'settings');
        const res = await api.post("/form/create", {
            title: formName,
            description: ""
        });
        if (res && res.form) {
            this.setState({ forms: [...this.state.forms, res.form] });
            this.modal("edit-form", { form: res.form });
        }
        this.props.loaded('settings');
    }

    saveForm = async (form) => {
        await this.props.loading(1005, '', false, 'settings');
        await api.post("/form/update", {
            formId: form._id,
            formDiff: form
        });
        // Local update
        let forms = [...this.state.forms];
        for (const f of forms) {
            if (f._id !== form._id) continue;
            f.title = form.title;
            f.description = form.description;
            f.lastModified = new Date(Date.now());
        }
        this.setState({ forms });

        this.props.loaded('settings');
    }

    createScript = async (scriptName) => {
        await this.props.loading(1005, '', false, 'settings');
        const res = await api.post("/script/create", { name: scriptName });
        if (res && res.script) {
            this.setState({ scripts: [...this.state.scripts, res.script] });
            this.modal("edit-script", { script: res.script });
        }
        this.props.loaded('settings');
    }

    saveScript = async (script) => {
        await this.props.loading(1005, '', false, 'settings');
        await api.post("/script/update", {
            scriptId: script._id,
            scriptDiff: {
                ...script,
                _id: undefined
            }
        });
        // Local update
        let scripts = [...this.state.scripts];
        for (const s of scripts) {
            if (s._id !== script._id) continue;
            s.name = script.name;
            s.requirements = script.requirements;
            s.source = script.source;
            s.schedule = script.schedule;
            s.language = script.language;
            s.lastModified = new Date(Date.now());
        }
        this.setState({ scripts });

        this.props.loaded('settings');
    }

    runScript = async (script) => {
        await this.props.loading(1005, '', false, 'settings');
        await api.get('/script/run?scriptId=' + script._id);
        toast.info("The script " + script.name + " has been queued and will run shortly!");
        this.props.loaded('settings');
    }

    clearScriptError = async (script) => {
        await this.props.loading(1005, '', false, 'settings');
        await api.post("/script/update", {
            scriptId: script._id,
            scriptDiff: {
                failed: false,
                error: null,
                traceback: null,
            },
        });

        // Local update
        let scripts = [...this.state.scripts];
        for (const s of scripts) {
            if (s._id !== script._id) continue;
            s.failed = false;
            s.error = undefined;
            s.traceback = undefined;
        }
        this.setState({ scripts });

        this.props.loaded('settings');
    }

    inviteUser = () => this.modal("invite-user");

    initGoogle = () => {
        if (!window.gapi) return;
        window.gapi.load('auth2', () => {
            this.googleAuth = window.gapi.auth2.init({
                client_id: '941349164286-5fl8ip5p3mqg25romcu2kotlumg5i5fo.apps.googleusercontent.com',
                // Scopes to request in addition to 'profile' and 'email'
                scope: "https://mail.google.com/"
            });
        })
    }

    requestGmailSetup = async () => {
        await this.props.loading(1005, '', false, 'settings');
        this.modal();

        const res = await api.post('/user/help', {
            title: "Request help setting up Google Account",
            comment: this.props.user.name + ` (` + this.props.user.email + `) from ` + this.props.team.name + ` has clicked on the "Help me setup" button in the Team Inbound Email settings page, and would like to be contacted and taken through the process of setting up their Google account on Stax·ai.`
        });

        if (res && res.success)
            toast.success("Your account manager will reach out to help you setup your account!");

        this.props.loaded('settings');
    }

    updateEmailAccount = async (config) => {

        if (!this.props.team || !this.props.team.email || !this.props.team.email.settings || !this.state.modalData || typeof this.state.modalData.accountIdx !== "number") return;

        let settings = [...this.props.team.email.settings];
        const idx = this.state.modalData.accountIdx;
        settings[idx] = {
            ...settings[idx],
            ...config
        };

        await this.props.updateTeam({
            ...this.props.team,
            email: {
                ...this.props.team.email,
                settings
            }
        });
    }

    addEmailAccount = async (config) => {

        try {

            const exists = this.props.team.email.settings.find(e => e.email === config.email);

            if (exists)
                return toast.error("Email address already exists! Delete that one first.");

            const res = await api.post('/team/email/verify', config);

            await this.props.loading(1005, '', false, 'settings');

            if (!res || !res.success)
                return this.modal();

            const accountIdx = this.props.team.email.settings.length;

            await this.props.updateTeam({
                ...this.props.team,
                email: {
                    ...this.props.team.email,
                    enableInbound: true, // Enable when account created
                    settings: [
                        ...this.props.team.email.settings,
                        {
                            ...config,
                            email: res.email ? res.email : config.email,
                            password: res.credentials ? res.credentials : config.password,
                            verified: true,
                            lastChecked: Date.now(),
                            bulk: false
                        }
                    ]
                }
            });

            this.modal("configure-email", { accountIdx });
            toast.success("Verified and added email");

            this.props.loaded('settings');

        } catch (error) {
            toast.error("Error verifying email");
        }
    }

    oAuth = async (provider) => {
        switch (provider) {

            case "microsoft":
                {
                    // Open a new window to login and grant access to the microsoft account
                    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=36082884-b6de-4039-bfca-b729106afb5c&response_type=code&redirect_uri=${api.publicUrl}/settings/email/&response_mode=query&scope=openid%20offline_access%20profile%20email%20https%3A%2F%2Fgraph.microsoft.com%2FMail.ReadWrite%20user.ReadWrite`
                    const authWindow = window.open(authUrl);
                    const authCode = await new Promise((resolve, reject) => {
                        setInterval(() => {
                            try {
                                const urlParams = new URLSearchParams(authWindow.location.search);
                                if (urlParams.has("code")) {
                                    const code = urlParams.get("code");
                                    authWindow.close();
                                    resolve(code)
                                }
                                else if (urlParams.has("error")) {
                                    authWindow.close();
                                    resolve({ error: "Access Denied" })
                                }
                            }
                            catch { }

                        }, 50)
                    })

                    // Well, there seems to have been an error (Window closed, access denied, etc)
                    if (authCode.error)
                        return authCode;

                    // Get the access token
                    const res = await api.post("/email/outlook/token", { code: authCode });
                    if (!res.success)
                        return { error: res.data.error }

                    return res.data;
                }
            default: {
                console.error("Unsupported email provider", provider);
            }
        }
    }

    render = () => {

        const savable = false; // TODO

        const team = this.props.team && this.props.team._id ? this.props.team : { _id: null, users: [] };

        return (<>

            <Navigation
                settings={true}
                team={this.props.team}
                user={this.props.user}
            />

            <User
                clearNotification={this.props.clearNotification}
                user={this.props.user}
                updateUser={this.props.updateUser}
                switchTeam={this.props.switchTeam}
                team={this.props.team}
            />

            <div className="main-title full">
                <div>
                    <Switch>
                        <Route
                            path="/settings/team" exact
                            render={() => <>
                                <div className="title-text">Team Users</div>
                                {<div
                                    className="title-icon"
                                    onClick={this.inviteUser}
                                >
                                    Invite new user
                                </div>}
                                {/* <ViewControl view={this.state.view} setView={view => this.setState({ view })} /> */}
                            </>}
                        />
                        <Route
                            path="/settings/email" exact
                            render={() => <div className="title-text">Inbound Email Settings</div>}
                        />
                        <Route
                            path="/settings/fax" exact
                            render={() => <>
                                <div className="title-text" style={{ float: 'left' }}>Inbound Fax Integration</div>
                                {this.state.changed && <div
                                    className="settings-save"
                                    title="Click to save changes..."
                                    onClick={() => {
                                        this.setState({ changed: false });
                                        this.props.updateTeam(this.props.team);
                                    }}
                                >Save</div>}
                            </>}
                        />
                        <Route
                            path="/settings/forms" exact
                            render={() => <div className="title-text">Intake Forms</div>}
                        />
                        <Route
                            path={["/settings/import", "/settings/import/:source"]} exact
                            render={() => <div className="title-text">Import and Sync Data</div>}
                        />
                        <Route
                            path={["/settings/automate", "/settings/automate/:stackId"]} exact
                            render={() => <div className="title-text">
                                <strong>Automate</strong> your document workflow
                            </div>}
                        />
                        <Route
                            path="/settings/scripts" exact
                            render={() => <div className="title-text">Scheduled Scripts</div>}
                        />
                        <Route
                            path="/settings/billing" exact
                            render={() => <div className="title-text">Billing, Plan, and Usage</div>}
                        />
                        <Route
                            path="/settings/plan-editor" exact
                            render={() => <div className="title-text">Manage Retirement Plans</div>}
                        />
                    </Switch>
                </div>
                {savable && <div
                    className="settings-save"
                    title="Click to save changes..."
                    onClick={this.saveChanges}
                >Save</div>}
            </div>

            <div className="main-content full">
                <Scrollbars>
                    <Switch>
                        <Route
                            path="/settings/team" exact
                            render={(props) => <TeamSettings
                                {...props}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                modal={this.modal}
                                user={this.props.user}
                                team={this.props.team}
                                owner={team.owner}
                                users={team.users}
                                inviteUser={this.inviteUser}
                                updateUser={this.props.updateUser}
                                deleteUser={this.props.deleteUser}
                                resetPassword={this.props.resetPassword}
                                updateTeam={(team, cloud = true) => {
                                    if (!cloud)
                                        this.setState({ changed: true });
                                    this.props.updateTeam(team, cloud);
                                }}
                            />}
                        />

                        <Route
                            path="/settings/email" exact
                            render={(props) => <InboundEmail
                                {...props}
                                team={team}
                                modal={this.modal}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                updateTeam={this.props.updateTeam}
                            />}
                        />

                        <Route
                            path="/settings/fax" exact
                            render={(props) => <InboundFax
                                {...props}
                                team={team}
                                modal={this.modal}
                                updateTeam={(team, cloud = true) => {
                                    if (!cloud)
                                        this.setState({ changed: true });
                                    this.props.updateTeam(team, cloud);
                                }}
                            />}
                        />

                        <Route
                            path="/settings/forms" exact
                            render={(props) => <Forms
                                {...props}
                                team={team}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                modal={this.modal}
                                forms={this.state.forms}
                                setForms={(forms) => this.setState({ forms })}
                                updateTeam={(team, cloud = true) => {
                                    if (!cloud)
                                        this.setState({ changed: true });
                                    this.props.updateTeam(team, cloud);
                                }}
                            />}
                        />

                        <Route
                            path={["/settings/import", "/settings/import/:source"]} exact
                            render={(props) => <Sync
                                {...props}
                                team={team}
                                users={team.users}
                                modal={this.modal}
                                updateTeam={(team, cloud = true) => {
                                    if (!cloud)
                                        this.setState({ changed: true });
                                    this.props.updateTeam(team, cloud);
                                }}
                            />}
                        />

                        <Route
                            path={["/settings/automate", "/settings/automate/:stackId"]} exact
                            render={(props) => <Automate
                                {...props}
                                team={team}
                                modal={this.modal}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                stacks={this.state.stacks}
                                loadStacks={this.loadStacks}
                                modules={this.state.modules}
                                setModules={modules => this.setState({ modules })}
                            />}
                        />

                        <Route
                            path="/settings/scripts" exact
                            render={(props) => <Scripts
                                {...props}
                                team={team}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                modal={this.modal}
                                scripts={this.state.scripts}
                                setScripts={(scripts) => this.setState({ scripts })}
                                runScript={this.runScript}
                                clearError={this.clearScriptError}
                                updateTeam={(team, cloud = true) => {
                                    if (!cloud)
                                        this.setState({ changed: true });
                                    this.props.updateTeam(team, cloud);
                                }}
                            />}
                        />

                        <Route
                            path="/settings/plan-editor"
                            render={(props) => <PlanEditor 
                                modal={this.modal}
                                team={team}
                                user={this.props.user}
                                updateTeam={this.props.updateTeam}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                                />
                        }
                        />

                        <Route
                            // default
                            render={(props) => <Billing
                                {...props}
                                user={this.props.user}
                                team={team}
                                users={team.users}
                                displayModal={this.state.modal}
                                modal={this.modal}
                                updateTeam={this.props.updateTeam}
                                loading={this.props.loading}
                                loaded={this.props.loaded}
                            />}
                        />
                    </Switch>
                </Scrollbars>
            </div>

            {/* Modal controller */}
            {this.state.modal === "invite-user" ? 
            <InviteUser
                modal={this.modal}
                team={this.props.team}
                inviteUser={this.props.inviteUser}
            /> :

            this.state.modal === "edit-form" ? 
            <EditForm
                modal={this.modal}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                data={this.state.modalData}
                saveForm={this.saveForm}
            /> :

            this.state.modal === "add-email-account" ?
            <AddEmailAccount
                modal={this.modal}
                addEmailAccount={this.addEmailAccount}
                helpRequest={this.requestGmailSetup}
                oAuth={this.oAuth}
            /> :

            this.state.modal === 'confirm-email'? 
            <ConfirmEmail
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:

            this.state.modal === 'confirm-email-year'? 
            <ConfirmEmailYear
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:

            this.state.modal === 'confirm-caution'? 
            <ConfirmCaution
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:

            this.state.modal === 'cancel-import'? 
            <CancelImport
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:
            
            this.state.modal === "run-ta" ?
            <RunTA
                modal={this.modal}
                user={this.props.user}
                team={this.props.team}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
            />:

            this.state.modal === "configure-email" ?
            <ConfigureEmail
                modal={this.modal}
                data={this.state.modalData}
                accounts={this.props.team && this.props.team.email && this.props.team.email.settings ? this.props.team.email.settings : []}
                updateEmailConfig={this.updateEmailAccount}
            /> :

            this.state.modal === "import-dropbox" ?
            <SyncDropbox
                modal={this.modal}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                stacks={this.state.stacks}
                loadStacks={this.loadStacks}
            /> :

            this.state.modal === "import-googledrive" ?
            <SyncGoogleDrive
                modal={this.modal}
                 mode="googledrive"
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                stacks={this.state.stacks}
                loadStacks={this.loadStacks}
            /> :

            this.state.modal === "import-onedrive" ?
            <SyncOneDrive
                modal={this.modal}
                mode="dropbox"
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
            /> :

            this.state.modal === "import-sharepoint" ?
            <SyncSharePoint
                modal={this.modal}
                mode="sharepoint"
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
            /> :
            this.state.modal === "import-box" ?
            <SyncBox
                modal={this.modal}
                mode="sharepoint"
                data={this.state.modalData}
                stacks={this.state.stacks}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                loadStacks={this.loadStacks}
            /> :
            this.state.modal === "import-smartvault" ?
            <SyncSmartVault
                modal={this.modal}
                mode="smartvault"
                data={this.state.modalData}
                stacks={this.state.stacks}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                loadStacks={this.loadStacks}
            /> :
            this.state.modal === "import-sharefile" ?
            <SyncShareFile
                modal={this.modal}
                mode="sharefile"
                data={this.state.modalData}
                stacks={this.state.stacks}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                loadStacks={this.loadStacks}
            /> :
            this.state.modal === "import-egnyte" ?
            <SyncEgnyte
                modal={this.modal}
                mode="egnyte"
                data={this.state.modalData}
                stacks={this.state.stacks}
                loading={this.props.loading}
                loaded={this.props.loaded}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
                loadStacks={this.loadStacks}
            /> :

            // this.state.modal === "import-pensionpro" ?
            // <SyncPensionPro
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-johnhancock" ?
            // <SyncJohnHancock
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-empower" ?
            // <SyncEmpower
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-voya" ?
            // <SyncVoya
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-ascensus" ?
            // <SyncAscensus
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-americanfundsrk" ?
            // <SyncAmericanFundsRK
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :

            // this.state.modal === "import-americanfundspp" ?
            // <SyncAmericanFundsPP
            //     modal={this.modal}
            //     data={this.state.modalData}
            //     loading={this.props.loading}
            //     loaded={this.props.loaded}
            //     stacks={this.state.stacks}
            //     loadStacks={this.loadStacks}
            //     team={this.props.team}
            //     user={this.props.user}
            //     updateTeam={this.props.updateTeam}
            // /> :
            this.state.modal === "import-desktop" ?
            <SyncDesktop
                modal={this.modal}
                data={this.state.modalData}
                loading={this.props.loading}
                loaded={this.props.loaded}
                stacks={this.state.stacks}
                loadStacks={this.loadStacks}
                team={this.props.team}
                user={this.props.user}
                updateTeam={this.props.updateTeam}
            /> :

            this.state.modal === "create-form" ?
            <CreateForm
                modal={this.modal}
                createForm={this.createForm}
            /> :

            this.state.modal === "create-script" ?
            <CreateScript
                modal={this.modal}
                createScript={this.createScript}
            /> :

            // Automation modules
            this.state.modal === "automate-attach-marketplace" ?
            <AttachFromMarketplace
                modal={this.modal}
                team={this.props.team}
                user={this.props.user}
                data={this.state.modalData}
            /> :

            this.state.modal === "automate-configure" ?
            <ConfigureModule
                modal={this.modal}
                data={this.state.modalData}
                team={this.props.team}
                user={this.props.user}
                users={team.users}
                stacks={this.state.stacks}
            /> :

            this.state.modal === "edit-script" ?
            <EditScript
                modal={this.modal}
                script={this.state.modalData.script}
                user={this.props.user}
                saveScript={this.saveScript}
            />
            
            : ""}

            {/* Tips */}
            {window.location.href.includes("email") && this.props.user && (!this.props.user.tips || !this.props.user.tips.email) && <EmailTips
                close={() => this.props.updateUser({
                    _id: this.props.user._id,
                    tips: {
                        ...this.props.user.tips,
                        email: true
                    }
                })}
            />}

        </>)
    }

};